import React, { useState, useEffect } from "react";
import axios from "../api/axiosInstance";
import { getCookie } from "../utils/cookies";
import { useNavigate } from "react-router-dom";
import { useLoading } from "../context/LoadingContext";

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6l3 3 5-5"/></svg>
);

const RegisterForm = () => {
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoading();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    rpassword: "",
  });

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRPassword, setShowRPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passTouched, setPassTouched] = useState(false);
  const [rpassTouched, setRpassTouched] = useState(false);

  // Live validations
  const emailHasAt = formData.email.includes('@');
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  const passMin6 = formData.password.length >= 6;
  const passUpper = /[A-Z]/.test(formData.password);
  const passNumber = /[0-9]/.test(formData.password);
  const passSpecial = /[!?,.@#$%^&*()_+\-=\[\]{};':"\\|<>\/]/.test(formData.password);
  const passMatch = formData.rpassword.length > 0 && formData.password === formData.rpassword;

  useEffect(() => {
    axios.get("/accounts/csrf/").catch(() => {});
    localStorage.removeItem("emailVerified");
    localStorage.removeItem("emailConfirmed");
    localStorage.removeItem("unverifiedEmail");
    localStorage.removeItem("verificationType");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const validateForm = () => {
    const newErrors = {};
    const nameRegex = /^[A-Za-zĂÎȘȚâăîșțóçéüöőűáúéíó]+(?:[\s'-][A-Za-zĂÎȘȚâăîșțóçéüöőűáúéíó]+)*$/u;

    Object.keys(formData).forEach((key) => {
      if (!formData[key]) newErrors[key] = "Acest câmp este obligatoriu!";
    });

    if (formData.first_name && !nameRegex.test(formData.first_name))
      newErrors.first_name = "Prenumele nu este valid!";
    if (formData.last_name && !nameRegex.test(formData.last_name))
      newErrors.last_name = "Numele nu este valid!";
    if (formData.email && !emailValid)
      newErrors.email = "Email invalid!";
    if (formData.password && (!passMin6 || !passUpper || !passNumber || !passSpecial))
      newErrors.password = "Parola nu îndeplinește condițiile!";
    if (formData.password !== formData.rpassword)
      newErrors.rpassword = "Parolele nu coincid!";
    if (!ageConfirmed)
      newErrors.ageConfirmed = "Trebuie să confirmați că aveți peste 16 ani.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    startLoading();

    if (!validateForm()) { stopLoading(); return; }

    const csrfToken = getCookie("csrftoken");
    const response = await axios.post("/accounts/register/", formData, {
      headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
    });

    if (response?.__handled) {
      const status = response.originalError.response?.status;
      const errorData = response.originalError.response?.data;

      if (status === 400) {
        if (errorData?.error === "Email already exists") {
          setErrors((prev) => ({ ...prev, email: "Email deja înregistrat!" }));
        } else if (errorData?.error === "Your account needs to be verified.") {
          setServerError("Contul tău nu este verificat. Vei fi redirecționat pentru verificare.");
          localStorage.setItem("unverifiedEmail", formData.email);
          localStorage.setItem("verificationType", "register");
          setTimeout(() => navigate("/confirm-email"), 3000);
          stopLoading();
        }
      } else {
        setServerError("A apărut o eroare. Încearcă mai târziu.");
      }
      stopLoading();
      return;
    }

    localStorage.setItem("unverifiedEmail", formData.email);
    localStorage.setItem("verificationType", "register");
    localStorage.setItem("emailConfirmed", "false");
    navigate("/confirm-email");
    stopLoading();
  };

  return (
    <form noValidate onSubmit={handleSubmit}>
      <div className="ar-grid">
        {/* NUME */}
        <div className="ar-field">
          <label className="ar-label">Nume</label>
          <div className="ar-input-wrap">
            <input className="ar-input" type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Popescu" />
          </div>
          {errors.last_name && <div className="ar-field-error">{errors.last_name}</div>}
        </div>

        {/* PRENUME */}
        <div className="ar-field">
          <label className="ar-label">Prenume</label>
          <div className="ar-input-wrap">
            <input className="ar-input" type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Maria" />
          </div>
          {errors.first_name && <div className="ar-field-error">{errors.first_name}</div>}
        </div>

        {/* EMAIL */}
        <div className="ar-field full">
          <label className="ar-label">Email</label>
          <div className="ar-input-wrap">
            <input
              className="ar-input"
              type="text"
              name="email"
              value={formData.email}
              onChange={(e) => { handleChange(e); if (!emailTouched) setEmailTouched(true); }}
              onFocus={() => { if (!emailTouched) setEmailTouched(true); }}
              placeholder="exemplu@email.com"
            />
          </div>
          {emailTouched && formData.email.length > 0 && (
            <div className="ar-checks">
              <div className={"ar-check" + (emailHasAt ? " valid" : "")}>
                <span className="ar-check-icon"><CheckIcon /></span>
                Conține simbolul @
              </div>
              <div className={"ar-check" + (emailValid ? " valid" : "")}>
                <span className="ar-check-icon"><CheckIcon /></span>
                Adresă de email validă
              </div>
            </div>
          )}
          {errors.email && <div className="ar-field-error">{errors.email}</div>}
        </div>

        {/* PAROLA */}
        <div className="ar-field">
          <label className="ar-label">Parolă</label>
          <div className="ar-input-wrap">
            <input
              className="ar-input"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={(e) => { handleChange(e); if (!passTouched) setPassTouched(true); }}
              onFocus={() => { if (!passTouched) setPassTouched(true); }}
              placeholder="••••••••"
              style={{ paddingRight: 40 }}
            />
            <button type="button" className="ar-eye" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          {passTouched && formData.password.length > 0 && (
            <div className="ar-checks">
              <div className={"ar-check" + (passMin6 ? " valid" : "")}><span className="ar-check-icon"><CheckIcon /></span>Minim 6 caractere</div>
              <div className={"ar-check" + (passUpper ? " valid" : "")}><span className="ar-check-icon"><CheckIcon /></span>O literă mare (A-Z)</div>
              <div className={"ar-check" + (passNumber ? " valid" : "")}><span className="ar-check-icon"><CheckIcon /></span>O cifră (0-9)</div>
              <div className={"ar-check" + (passSpecial ? " valid" : "")}><span className="ar-check-icon"><CheckIcon /></span>Un caracter special (!@#$...)</div>
            </div>
          )}
          {errors.password && <div className="ar-field-error">{errors.password}</div>}
        </div>

        {/* REPETĂ PAROLA */}
        <div className="ar-field">
          <label className="ar-label">Repetă parola</label>
          <div className="ar-input-wrap">
            <input
              className="ar-input"
              type={showRPassword ? "text" : "password"}
              name="rpassword"
              value={formData.rpassword}
              onChange={(e) => { handleChange(e); if (!rpassTouched) setRpassTouched(true); }}
              onFocus={() => { if (!rpassTouched) setRpassTouched(true); }}
              placeholder="••••••••"
              style={{ paddingRight: 40 }}
            />
            <button type="button" className="ar-eye" onClick={() => setShowRPassword(!showRPassword)} tabIndex={-1}>
              {showRPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              )}
            </button>
          </div>
          {rpassTouched && formData.rpassword.length > 0 && (
            <div className="ar-checks">
              <div className={"ar-check" + (passMatch ? " valid" : "")}><span className="ar-check-icon"><CheckIcon /></span>Parolele coincid</div>
            </div>
          )}
          {errors.rpassword && <div className="ar-field-error">{errors.rpassword}</div>}
        </div>
      </div>

      <label className="ar-age">
        <input
          type="checkbox"
          checked={ageConfirmed}
          onChange={(e) => {
            setAgeConfirmed(e.target.checked);
            setErrors((prev) => ({ ...prev, ageConfirmed: "" }));
          }}
        />
        <span>Confirm că am peste 16 ani.</span>
      </label>
      {errors.ageConfirmed && <div className="ar-field-error">{errors.ageConfirmed}</div>}

      {serverError && <div className="ar-server-error">{serverError}</div>}

      <button type="submit" className="ar-submit"><span>Înregistrează-te</span></button>

      <div className="ar-redirect">
        Ai deja un cont?
        <a href="/login">Autentifică-te aici</a>
      </div>
    </form>
  );
};

export default RegisterForm;
